import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { AdminRole, type AdminUserRow } from '@etiquetachef/types';
import { useAuth } from '@/auth/AuthProvider';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { useAdminUsers } from '@/features/superAdmins/useAdminUsers';
import { useSuperAdminAuditTrail } from '@/features/superAdmins/useSuperAdminAuditTrail';
import { AddSuperAdminDialog } from '@/features/superAdmins/AddSuperAdminDialog';
import { RemoveSuperAdminDialog } from '@/features/superAdmins/RemoveSuperAdminDialog';
import type { AuditEventRecord } from '@/lib/firestoreAudit';
import { formatDateTime } from '@/lib/format';

type RemoveTarget = { uid: string; email: string | null } | null;

type Row = AdminUserRow & {
  /** Concessão mais recente nas entradas recentes do audit — pode faltar. */
  grant: AuditEventRecord | undefined;
  isSelf: boolean;
};

/**
 * Tela `/settings/super-admins` — quem tem acesso ao painel admin, com
 * conceder (`setSuperAdmin`) e revogar (`removeSuperAdmin`).
 *
 * Origem: 1f372df7a — 2026-04-26 17:49 +0200 (Sprint 2; sem issue no commit).
 *
 * Por quê / Falhas conhecidas:
 *  - Até 2026-10-03 (#795, ADM-6) a lista era reconstruída das últimas 100
 *    entradas de `/audit` (todas as ações): admin concedido há mais tempo
 *    sumia e não podia ser revogado. Agora vem do Firebase Auth
 *    (`listAdminUsers`); o audit fica só no rótulo "concedido em X por Y".
 *  - Até 2026-10-03 (#795, ADM-2) as duas callables aceitavam qualquer papel;
 *    hoje são ROOT-only, e a tela esconde as ações de quem não é ROOT
 *    (`listAdminUsers` responde `permission-denied` → `ROOT_ONLY`).
 *
 * Contrato: recarrega a lista depois de conceder/revogar (`onSuccess` dos
 * diálogos). Falha de carga → `Alert` + `reportNonFatal` (no hook); falha do
 * audit (secundário) → só report, a lista continua.
 *
 * Usado por: rota `settings/super-admins` (`routes/router.tsx`), menu
 * Sistema (`AdminLayout`). Spec: `docs/screens/super-admins-admin.md`.
 *
 * Histórico:
 *  - 2026-10-03 14:13 +0200 — (#795, ADM-2/ADM-6) lista do Auth, papel e
 *    último login por linha, estado ROOT_ONLY.
 */
export function SuperAdminsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { status } = useAuth();
  const { state, reload } = useAdminUsers();
  const trail = useSuperAdminAuditTrail();
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget>(null);

  const currentUid = status.kind === 'AUTHORIZED' ? status.user.uid : null;

  // O audit é secundário: a lista continua sem ele, mas a falha não pode
  // sumir em silêncio (CLAUDE.md, "Observabilidade").
  useEffect(() => {
    if (trail.kind === 'ERROR') {
      reportNonFatal('admin_super_admins_audit_trail_failed', serializeError(trail.error));
    }
  }, [trail]);

  const rows: Row[] = useMemo(() => {
    if (state.kind !== 'READY') return [];
    const grants = trail.kind === 'READY' ? trail.latestGrantByUid : undefined;
    return state.admins.map((admin) => ({
      ...admin,
      grant: grants?.get(admin.uid),
      isSelf: admin.uid === currentUid,
    }));
  }, [state, trail, currentUid]);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('superAdmins.subtitle')}
          actions={
            state.kind === 'ROOT_ONLY' ? undefined : (
              <Button
                variant="primary"
                onClick={(): void => {
                  setAddOpen(true);
                }}
              >
                {t('superAdmins.add.button')}
              </Button>
            )
          }
        >
          {t('superAdmins.title')}
        </Header>
      }
    >
      {state.kind === 'LOADING' && (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      )}
      {state.kind === 'ROOT_ONLY' && <Alert type="info">{t('superAdmins.errors.rootOnly')}</Alert>}
      {state.kind === 'ERROR' && <Alert type="error">{t('superAdmins.errors.loadFailed')}</Alert>}
      {state.kind === 'READY' && (
        <Table<Row>
          items={rows}
          trackBy="uid"
          variant="container"
          header={
            <Header variant="h2" description={t('superAdmins.active.fromAuth')}>
              {t('superAdmins.active.title')}
            </Header>
          }
          empty={<Box textAlign="center" color="inherit">{t('superAdmins.active.empty')}</Box>}
          columnDefinitions={[
            {
              id: 'account',
              header: t('superAdmins.active.title'),
              cell: (row): React.ReactNode => (
                <SpaceBetween size="xxs">
                  {row.email && <Box variant="strong">{row.email}</Box>}
                  <Box fontSize="body-s" color="text-status-inactive">
                    <span style={{ fontFamily: 'monospace' }}>{row.uid}</span>
                  </Box>
                  {row.grant?.atMillis != null && (
                    <Box fontSize="body-s" color="text-status-inactive">
                      {t('superAdmins.active.grantedAt', {
                        when: formatDateTime(row.grant.atMillis, i18n.language),
                        actor: row.grant.actorEmail,
                      })}
                    </Box>
                  )}
                </SpaceBetween>
              ),
            },
            {
              id: 'role',
              header: t('superAdmins.active.role'),
              cell: (row): React.ReactNode => (
                <SpaceBetween size="xxs">
                  <Badge color={row.role === AdminRole.ROOT ? 'red' : 'blue'}>{row.role}</Badge>
                  {row.legacyClaim && (
                    <Box fontSize="body-s" color="text-status-inactive">
                      {t('superAdmins.active.legacy')}
                    </Box>
                  )}
                  {row.disabled && (
                    <Box fontSize="body-s" color="text-status-error">
                      {t('superAdmins.active.disabled')}
                    </Box>
                  )}
                  {/* #795 (ADM-3): claim em conta de e-mail não verificado é
                      sinal de pré-sequestro — a porta recusa, mas as Rules
                      ainda deixam ler; a ROOT precisa ver para revogar. */}
                  {!row.emailVerified && (
                    <Box fontSize="body-s" color="text-status-error">
                      {t('superAdmins.active.unverified')}
                    </Box>
                  )}
                </SpaceBetween>
              ),
            },
            {
              id: 'lastSignIn',
              header: '',
              cell: (row): React.ReactNode => (
                <Box fontSize="body-s" color="text-status-inactive">
                  {row.lastSignInAtMillis === null
                    ? t('superAdmins.active.neverSignedIn')
                    : t('superAdmins.active.lastSignIn', {
                        when: formatDateTime(row.lastSignInAtMillis, i18n.language),
                      })}
                </Box>
              ),
            },
            {
              id: 'actions',
              header: '',
              cell: (row): React.ReactNode => (
                <Button
                  disabled={row.isSelf}
                  onClick={(): void => {
                    setRemoveTarget({ uid: row.uid, email: row.email });
                  }}
                >
                  {t('superAdmins.remove.button')}
                </Button>
              ),
            },
          ]}
        />
      )}

      <AddSuperAdminDialog
        open={addOpen}
        onClose={(): void => {
          setAddOpen(false);
        }}
        onSuccess={reload}
      />
      <RemoveSuperAdminDialog
        open={removeTarget !== null}
        uid={removeTarget?.uid ?? null}
        email={removeTarget?.email ?? null}
        onClose={(): void => {
          setRemoveTarget(null);
        }}
        onSuccess={reload}
      />
    </ContentLayout>
  );
}
